import axiosInstance from "./axiosConfig";

// API endpoint for attributes
const ATTRIBUTES_URL = "/attributes";

/**
 * Get all attributes
 * @param {Object} params - Query parameters
 * @returns {Array} List of attributes
 */
const getAttributes = async (params = {}) => {
  const response = await axiosInstance.get(ATTRIBUTES_URL, { params });
  return response.data;
};

/**
 * Get attribute by ID
 * @param {string} id - Attribute ID
 * @returns {Object} Attribute data
 */
const getAttributeById = async (id) => {
  const response = await axiosInstance.get(`${ATTRIBUTES_URL}/${id}`);
  return response.data;
};

/**
 * Create a new attribute
 * @param {Object} attributeData - Attribute data to create
 * @returns {Object} Created attribute data
 */
const createAttribute = async (attributeData) => {
  const response = await axiosInstance.post(ATTRIBUTES_URL, attributeData);
  return response.data;
};

/**
 * Update attribute
 * @param {string} id - Attribute ID
 * @param {Object} attributeData - Attribute data to update
 * @returns {Object} Updated attribute data
 */
const updateAttribute = async (id, attributeData) => {
  const response = await axiosInstance.put(
    `${ATTRIBUTES_URL}/${id}`,
    attributeData
  );
  return response.data;
};

/**
 * Delete attribute
 * @param {string} id - Attribute ID
 * @returns {Object} Success message
 */
const deleteAttribute = async (id) => {
  const response = await axiosInstance.delete(`${ATTRIBUTES_URL}/${id}`);
  return response.data;
};

/**
 * Get attribute values for an attribute
 * @param {string} attributeId - Attribute ID
 * @returns {Array} List of attribute values
 */
const getAttributeValues = async (attributeId) => {
  const response = await axiosInstance.get(
    `${ATTRIBUTES_URL}/${attributeId}/values`
  );
  return response.data;
};

/**
 * Create a new attribute value
 * @param {string} attributeId - Attribute ID
 * @param {Object} valueData - Attribute value data
 * @returns {Object} Created attribute value
 */
const createAttributeValue = async (attributeId, valueData) => {
  const response = await axiosInstance.post(
    `${ATTRIBUTES_URL}/${attributeId}/values`,
    valueData
  );
  return response.data;
};

/**
 * Update attribute value
 * @param {string} attributeId - Attribute ID
 * @param {string} valueId - Attribute value ID
 * @param {Object} valueData - Attribute value data to update
 * @returns {Object} Updated attribute value
 */
const updateAttributeValue = async (attributeId, valueId, valueData) => {
  const response = await axiosInstance.put(
    `${ATTRIBUTES_URL}/${attributeId}/values/${valueId}`,
    valueData
  );
  return response.data;
};

/**
 * Delete attribute value
 * @param {string} attributeId - Attribute ID
 * @param {string} valueId - Attribute value ID
 * @returns {Object} Success message
 */
const deleteAttributeValue = async (attributeId, valueId) => {
  const response = await axiosInstance.delete(
    `${ATTRIBUTES_URL}/${attributeId}/values/${valueId}`
  );
  return response.data;
};

/**
 * Bulk reorder attribute values
 * @param {string} attributeId - Attribute ID
 * @param {Array} order - Array of { id, displayOrder }
 * @returns {Array} Updated attribute values
 */
const reorderAttributeValues = async (attributeId, order) => {
  const response = await axiosInstance.put(
    `${ATTRIBUTES_URL}/${attributeId}/values/reorder`,
    { order }
  );
  return response.data;
};

const attributeService = {
  getAttributes,
  getAttributeById,
  createAttribute,
  updateAttribute,
  deleteAttribute,
  getAttributeValues,
  createAttributeValue,
  updateAttributeValue,
  deleteAttributeValue,
  reorderAttributeValues,
};

export default attributeService;
